import { dayjs } from "utils";
import bigDecimal from "js-big-decimal";
import {
  Financial,
  MarketScheduleResponse,
  Stock,
  StockMarketEnum,
  StockMarketStateEnum,
} from "../features/api/openData";
import { MaybeTradingData } from "@hooks/useStockDetailsMarket";
import { LOCALES } from "./i18n.locale";
import { formatBigNumber, FormatNumberOptions } from "./formatBigNumber";
import Translations from "@translate/utils/stock";
import { I18nUtil } from "@utils/i18n";
const I18n = I18nUtil.createScoped(Translations);
// type Financial = any;

export const parseStockPathName = (pathName: string) => {
  let path = pathName;
  Object.values(LOCALES).forEach((locale) => {
    path = path.replace(`/${locale}/`, "/");
  });
  const [_, __, _symbol, module] = path.split("/");
  return {
    symbol: _symbol,
    module: module ? module?.toLocaleLowerCase?.() : "",
  };
};

type ICBMap = {
  handleXLabel: (date: dayjs.Dayjs) => string;
  handleTickLabel: (date: dayjs.Dayjs) => string;
};

export const genForecastLineData = (
  ohlc: Array<{
    time: number;
    open: string;
    high: string;
    low: string;
    close: string;
    volume?: string;
  }>,
  cbOption: ICBMap,
): {
  XAxis: Array<{ x: number; label: string }>;
  YAxisData: Array<number>;
  ticks: Array<{ x: number; label: string }>;
} => {
  if (!Array.isArray(ohlc) || ohlc.length === 0) {
    return { XAxis: [], YAxisData: [], ticks: [] };
  }
  const FIVE_DAY = 5 * 24 * 60 * 60 * 1000;
  const XAxis: Array<{ x: number; label: string }> = [];
  const YAxisData: Array<number> = [];

  // 从最后一个数据点开始倒推采样，确保最新数据被包含
  let start = ohlc[ohlc.length - 1].time;
  let i = ohlc.length - 1;
  // 倒序遍历数据，从最新数据开始向前采样
  while (i >= 0) {
    // 如果当前数据点的时间在采样范围内（小于等于当前采样点时间）
    if (ohlc[i].time <= start) {
      // 将数据点插入到数组开头，保持时间顺序
      XAxis.unshift({
        x: ohlc[i].time,
        label: cbOption.handleXLabel(dayjs(ohlc[i].time)),
      });
      YAxisData.unshift(Number(ohlc[i].close));
      // 更新下一个采样点：向前推5天
      start = dayjs(ohlc[i].time).startOf("day").valueOf() - FIVE_DAY;
    }
    i--;
  }

  const step = Math.floor(XAxis.length / 3);
  const ticks: Array<{ x: number; label: string }> = [
    { x: XAxis[step].x, label: cbOption.handleTickLabel(dayjs(XAxis[step].x)) },
    {
      x: XAxis[step * 2].x,
      label: cbOption.handleTickLabel(dayjs(XAxis[step * 2].x)),
    },
    {
      x: XAxis[XAxis.length - 1].x,
      label: cbOption.handleTickLabel(dayjs(XAxis[XAxis.length - 1].x)),
    },
  ];
  return { XAxis, YAxisData, ticks };
};

// ============================================================================
// Factory Functions for Financial Data Processing
// ============================================================================

/**
 * 值格式化配置选项
 */
interface ValueFormatterOptions {
  /** 是否转换为百分比显示（会乘以 100 并添加 % 符号） */
  asPercentage?: boolean;
  /** formatBigNumber 的配置选项 */
  formatOptions?: FormatNumberOptions;
}

/**
 * 格式化财务数值
 * @param value - 要格式化的值
 * @param options - 格式化选项
 * @returns 格式化后的值
 */
const formatFinancialValue = (
  value: any,
  options?: ValueFormatterOptions,
): any => {
  // 如果没有传任何格式化参数，直接返回原值
  if (!options?.asPercentage && !options?.formatOptions) {
    return value;
  }

  // 检查是否是 string 或 number 类型
  const isFormattable = typeof value === "string" || typeof value === "number";
  if (!isFormattable) {
    return value; // 非 string/number 类型，直接返回
  }

  let processedValue = value;

  // 如果需要百分比显示，先乘以 100
  if (options?.asPercentage) {
    processedValue = Number(value) * 100;
  }

  // 调用 formatBigNumber 进行格式化
  const formatted = formatBigNumber(processedValue, options?.formatOptions);

  // 如果是百分比，添加 % 符号
  if (options?.asPercentage) {
    return `${formatted}%`;
  }

  return formatted;
};

/**
 * 创建简单字段取值的 getValue 函数
 * @param fieldName - 要提取的字段名
 * @param options - 配置选项
 * @returns getValue 函数
 */
const createSimpleGetter = (
  fieldName: keyof Financial,
  options: {
    excludeTTM?: boolean;
    asPercentage?: boolean;
    formatOptions?: FormatNumberOptions;
  } = {},
) => {
  return (financials: Financial[]) => {
    const data: Record<string, Maybe<string | number>> = {};
    const { excludeTTM, ...formatterOptions } = options;

    financials.forEach((financial: Financial) => {
      // 处理 TTM 过滤逻辑
      if (excludeTTM && financial.freq === "ttm") {
        return;
      }

      const key = financial.freq === "ttm" ? "ttm" : (financial.period ?? "");
      const rawValue = financial[fieldName];

      // 应用格式化
      data[key] = formatFinancialValue(rawValue, formatterOptions);
    });
    data.id = Math.floor(Math.random() * 10000000);
    return data;
  };
};

/**
 * 创建计算型 getValue 函数
 * @param calculator - 计算函数，接收 financial 返回计算结果或 null/undefined
 * @param options - 配置选项
 * @returns getValue 函数
 */
const createCalculatedGetter = (
  calculator: (financial: Financial) => number | null | undefined,
  options: {
    excludeTTM?: boolean;
    asPercentage?: boolean;
    formatOptions?: FormatNumberOptions;
  } = {},
) => {
  return (financials: Financial[]) => {
    const data: Record<string, Maybe<string | number>> = {};
    const { excludeTTM, ...formatterOptions } = options;

    financials.forEach((financial: Financial) => {
      if (excludeTTM && financial.freq === "ttm") {
        return;
      }

      const value = calculator(financial);
      if (value !== null && value !== undefined) {
        const key = financial.freq === "ttm" ? "ttm" : (financial.period ?? "");

        // 应用格式化
        data[key] = formatFinancialValue(value, formatterOptions);
      }
    });
    data.id = Math.floor(Math.random() * 10000000);
    return data;
  };
};

// ============================================================================
// Income Statement (IC) Data Configuration
// ============================================================================

export const getICData = () => {
  const list = [
    {
      breakdown: I18n.t("Revenue"),
      getValue: createSimpleGetter("revenue", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Revenue Growth (YoY)"),
      getValue: createSimpleGetter("revenue_growth_yoy", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Cost of Revenue"),
      getValue: createSimpleGetter("cost_of_goods_sold", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Gross Profit"),
      getValue: createSimpleGetter("gross_income", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Selling, General & Admin"),
      getValue: createSimpleGetter("sga_expense", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Research & Development"),
      getValue: createSimpleGetter("research_development", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Operating Expenses"),
      getValue: createSimpleGetter("total_operating_expense", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 这个后端没有
    // {
    //   breakdown: "~Operating Income~",
    //   getValue: createSimpleGetter("total_operating_expense", { formatOptions: { needFormat: true, trimZeros: true, roundingMode: bigDecimal.RoundingModes.DOWN, precision: 2 } }),
    // },
    // 这个后端没有
    // {
    //   breakdown: "~Interest Expense~",
    // },
    // 这个后端没有
    // {
    //   breakdown: "~Interest & Investment Income~",
    // },
    {
      breakdown: I18n.t("Other Non Operating Income (Expenses)"),
      getValue: createSimpleGetter("total_other_income_expense_net", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Pretax Income"),
      getValue: createSimpleGetter("pretax_income", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Income Tax Expense"),
      getValue: createSimpleGetter("provision_for_income_taxes", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Net Income"),
      getValue: createSimpleGetter("net_income", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 这个后端没有
    // {
    //   breakdown: "~Net Income to Common~",
    // },
    {
      breakdown: I18n.t("Net Income Growth"),
      getValue: createSimpleGetter("net_income_growth", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 这个后端没有
    // {
    //   breakdown: "~Shares Outstanding (Basic)~",
    // },
    {
      breakdown: I18n.t("Shares Outstanding (Diluted)"),
      getValue: createSimpleGetter("diluted_average_shares_outstanding", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Shares Change (YoY)"),
      getValue: createSimpleGetter("shares_change_yoy", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~EPS (Basic)~",
    //   getValue: createSimpleGetter("diluted_average_shares_outstanding", { formatOptions: { needFormat: true, trimZeros: true, roundingMode: bigDecimal.RoundingModes.DOWN, precision: 2 } }),
    // },
    {
      breakdown: I18n.t("EPS (Diluted)"),
      getValue: createSimpleGetter("diluted_eps", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("EPS Growth"),
      getValue: createSimpleGetter("eps_growth", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Free Cash Flow"),
      getValue: createSimpleGetter("fcf", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Free Cash Flow Per Share"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.fcf !== "number" ||
            typeof f.diluted_average_shares_outstanding !== "number"
          ) {
            return null;
          }
          return f.fcf / f.diluted_average_shares_outstanding; // ✅ 前端计算
        },
        {
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    // 后端可能拿不到 能拿到也要根据TTM和正常数据分开取值
    // {
    //   breakdown: "~Dividend Per Share~",
    //   getValue: createSimpleGetter("diluted_average_shares_outstanding", {
    //     formatOptions: {
    //       needFormat: true,
    //       trimZeros: true,
    //       roundingMode: bigDecimal.RoundingModes.DOWN,
    //       precision: 2,
    //     },
    //   }),
    // },
    // 后端可能拿不到
    // {
    //   breakdown: "~Dividend Growth~",
    //   getValue: createSimpleGetter("diluted_average_shares_outstanding", { formatOptions: { needFormat: true, trimZeros: true, roundingMode: bigDecimal.RoundingModes.DOWN, precision: 2 } }),
    // },
    {
      breakdown: I18n.t("Gross Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.gross_income !== "number" ||
            typeof f.revenue !== "number"
          ) {
            return null;
          }
          return f.gross_income / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Operating Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (typeof f.ebit !== "number" || typeof f.revenue !== "number") {
            return null;
          }
          return f.ebit / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Profit Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.net_income !== "number" ||
            typeof f.revenue !== "number"
          ) {
            return null;
          }
          return f.net_income / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Free Cash Flow Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (typeof f.fcf !== "number" || typeof f.revenue !== "number") {
            return null;
          }
          return f.fcf / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("EBITDA"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.ebit !== "number" ||
            typeof f.depreciation_amortization !== "number"
          ) {
            return null;
          }
          return f.ebit + f.depreciation_amortization; // ❌ 前端计算  可能还要处理成100%
        },
        {
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("EBITDA Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.ebit !== "number" ||
            typeof f.depreciation_amortization !== "number" ||
            typeof f.revenue !== "number"
          ) {
            return null;
          }
          return (f.ebit + f.depreciation_amortization) / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("D&A For EBITDA"),
      getValue: createSimpleGetter("depreciation_amortization", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("EBIT"),
      getValue: createSimpleGetter("ebit", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("EBIT Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (typeof f.ebit !== "number" || typeof f.revenue !== "number") {
            return null;
          }
          return f.ebit / f.revenue; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Effective Tax Rate"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.provision_for_income_taxes !== "number" ||
            typeof f.pretax_income !== "number"
          ) {
            return null;
          }
          return f.provision_for_income_taxes / f.pretax_income; // ❌ 前端计算  可能还要处理成100%
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Revenue as Reported~",
    // },
  ];
  return list;
};

// ============================================================================
// Balance Sheet (BS) Data Configuration
// ============================================================================

export const getBSData = () => {
  const list = [
    {
      breakdown: I18n.t("Cash & Equivalents"),
      getValue: createSimpleGetter("cash_equivalents", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Short-Term Investments"),
      getValue: createSimpleGetter("short_term_investments", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Cash & Short-Term Investments"),
      getValue: createSimpleGetter("cash_short_term_investments", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Cash Growth"),
      getValue: createSimpleGetter("cash_growth", {
        excludeTTM: true,
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Accounts Receivable"),
      getValue: createSimpleGetter("accounts_receivables", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Other Receivables"),
      getValue: createSimpleGetter("other_receivables", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Receivables"),
      getValue: createSimpleGetter("total_receivables", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Inventory"),
      getValue: createSimpleGetter("inventory", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Other Current Assets"),
      getValue: createSimpleGetter("other_current_assets", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Current Assets"),
      getValue: createSimpleGetter("current_assets", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Property, Plant & Equipment"),
      getValue: createSimpleGetter("property_plant_equipment", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Long-Term Investments"),
      getValue: createSimpleGetter("long_term_investments", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Long-Term Deferred Tax Assets~",
    // },
    {
      breakdown: I18n.t("Other Long-Term Assets"),
      getValue: createSimpleGetter("other_long_term_assets", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Assets"),
      getValue: createSimpleGetter("total_assets", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Accounts Payable"),
      getValue: createSimpleGetter("accounts_payable", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Short-Term Debt"),
      getValue: createSimpleGetter("short_term_debt", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Current Portion of Long-Term Debt"),
      getValue: createSimpleGetter("current_portion_long_term_debt", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Current Portion of Leases~",
    // },
    // 后端拿不到
    // {
    //   breakdown: "~Current Income Taxes Payable~",
    // },
    // 后端拿不到
    // {
    //   breakdown: "~Current Unearned Revenue~",
    // },
    {
      breakdown: I18n.t("Other Current Liabilities"),
      getValue: createSimpleGetter("other_current_liabilities", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Current Liabilities"),
      getValue: createSimpleGetter("current_liabilities", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Long-Term Debt"),
      getValue: createSimpleGetter("long_term_debt", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Long-Term Leases~",
    // },
    {
      breakdown: I18n.t("Other Long-Term Liabilities"),
      getValue: createSimpleGetter("other_liabilities", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Liabilities"),
      getValue: createSimpleGetter("total_liabilities", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Common Stock"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.common_stock !== "number" ||
            typeof f.additional_paid_in_capital !== "number"
          ) {
            return null;
          }
          return f.common_stock + f.additional_paid_in_capital; // ✅ 前端计算
        },
        {
          excludeTTM: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Retained Earnings"),
      getValue: createSimpleGetter("retained_earnings", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Comprehensive Income & Other"),
      getValue: createSimpleGetter("other_equity", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Shareholders' Equity"),
      getValue: createSimpleGetter("total_equity", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Liabilities & Equity"),
      getValue: createSimpleGetter("liabilities_shareholders_equity", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Debt"),
      getValue: createSimpleGetter("total_debt", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Net Cash (Debt)"),
      getValue: createSimpleGetter("net_debt", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // {
    //   breakdown: I18n.t("Net Cash Growth"),
    //   getValue: createSimpleGetter("net_cash_growth", {
    //     excludeTTM: true,
    //     asPercentage: true,
    //     formatOptions: {
    //       needFormat: true,
    //       trimZeros: true,
    //       roundingMode: bigDecimal.RoundingModes.DOWN,
    //       precision: 2,
    //     },
    //   }),
    // },
    {
      breakdown: I18n.t("Net Cash Per Share"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.net_debt !== "number" ||
            typeof f.shares_outstanding !== "number"
          ) {
            return null;
          }
          return f.net_debt / f.shares_outstanding; // ✅ 前端计算
        },
        {
          excludeTTM: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Filing Date Shares Outstanding"),
      getValue: createSimpleGetter("shares_outstanding", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Total Common Shares Outstanding"),
      getValue: createSimpleGetter("shares_outstanding", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Working Capital"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.current_assets !== "number" ||
            typeof f.current_liabilities !== "number"
          ) {
            return null;
          }
          return f.current_assets - f.current_liabilities; // ✅ 前端计算
        },
        {
          excludeTTM: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Book Value Per Share"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.total_equity !== "number" ||
            typeof f.shares_outstanding !== "number"
          ) {
            return null;
          }
          return f.total_equity / f.shares_outstanding; // ✅ 前端计算
        },
        {
          excludeTTM: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Tangible Book Value~",
    // },
    {
      breakdown: I18n.t("Tangible Book Value Per Share"),
      getValue: createSimpleGetter("tangible_book_value_per_share", {
        excludeTTM: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Land~",
    // },
    // 后端拿不到
    // {
    //   breakdown: "~Machinery~",
    // },
    // 后端拿不到
    // {
    //   breakdown: "~Leasehold Improvements~",
    // },
  ];
  return list;
};

// ============================================================================
// Cash Flow Statement (CF) Data Configuration
// ============================================================================

export const getCFData = () => {
  const list = [
    {
      breakdown: I18n.t("Net Income"),
      getValue: createSimpleGetter("net_income_starting_line", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Depreciation & Amortization"),
      getValue: createSimpleGetter("depreciation_amortization", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Stock-Based Compensation"),
      getValue: createSimpleGetter("stock_based_compensation", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Other Operating Activities"),
      getValue: createSimpleGetter("other_funds_non_cash_items", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // 后端拿不到
    // {
    //   breakdown: "~Change in Accounts Receivable~",
    // },
    // {
    //   breakdown: "~Change in Inventory~",
    // },
    // {
    //   breakdown: "~Change in Accounts Payable~",
    // },
    // {
    //   breakdown: "~Change in Unearned Revenue~",
    // },
    // {
    //   breakdown: "~Change in Other Net Operating Assets~",
    // },
    {
      breakdown: I18n.t("Operating Cash Flow"),
      getValue: createSimpleGetter("net_operating_cash_flow", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Operating Cash Flow Growth"),
      getValue: createSimpleGetter("operating_cash_flow_growth", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Capital Expenditures"),
      getValue: createSimpleGetter("capex", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // {
    //   breakdown: "~Cash Acquisitions~",
    // },
    // {
    //   breakdown: "~Investment in Securities~",
    // },
    {
      breakdown: I18n.t("Other Investing Activities"),
      getValue: createSimpleGetter("other_investing_cash_flow_items_total", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Investing Cash Flow"),
      getValue: createSimpleGetter("net_investing_cash_flow", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // {
    //   breakdown: "~Short-Term Debt Issued~",
    // },
    // {
    //   breakdown: "~Long-Term Debt Issued~",
    // },
    // {
    //   breakdown: "~Total Debt Issued~",
    // },
    // {
    //   breakdown: "~Short-Term Debt Repaid~",
    // },
    // {
    //   breakdown: "~Long-Term Debt Repaid~",
    // },
    // {
    //   breakdown: "~Total Debt Repaid~",
    // },
    {
      breakdown: I18n.t("Net Debt Issued (Repaid)"),
      getValue: createSimpleGetter("issuance_reduction_debt_net", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Repurchase of Common Stock"),
      getValue: createSimpleGetter("issuance_reduction_capital_stock", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Common Dividends Paid"),
      getValue: createSimpleGetter("cash_dividends_paid", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Other Financing Activities"),
      getValue: createSimpleGetter("other_funds_financing_items", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Financing Cash Flow"),
      getValue: createSimpleGetter("net_cash_financing_activities", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Net Cash Flow"),
      getValue: createSimpleGetter("change_in_cash", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Free Cash Flow"),
      getValue: createSimpleGetter("fcf", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Free Cash Flow Growth"),
      getValue: createSimpleGetter("fcf_growth", {
        asPercentage: true,
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    {
      breakdown: I18n.t("Free Cash Flow Margin"),
      getValue: createCalculatedGetter(
        (f) => {
          if (typeof f.fcf !== "number" || typeof f.revenue !== "number") {
            return null;
          }
          return f.fcf / f.revenue;
        },
        {
          asPercentage: true,
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    {
      breakdown: I18n.t("Free Cash Flow Per Share"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.fcf !== "number" ||
            typeof f.diluted_average_shares_outstanding !== "number"
          ) {
            return null;
          }
          return f.fcf / f.diluted_average_shares_outstanding;
        },
        {
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
    // {
    //   breakdown: "~Cash Interest Paid~",
    // },
    {
      breakdown: I18n.t("Cash Income Tax Paid"),
      getValue: createSimpleGetter("cash_taxes_paid", {
        formatOptions: {
          needFormat: true,
          trimZeros: true,
          roundingMode: bigDecimal.RoundingModes.DOWN,
          precision: 2,
        },
      }),
    },
    // {
    //   breakdown: "~Levered Free Cash Flow~",
    // },
    {
      breakdown: I18n.t("Unlevered Free Cash Flow"),
      getValue: createCalculatedGetter(
        (f) => {
          if (
            typeof f.ebit !== "number" ||
            typeof f.provision_for_income_taxes !== "number" ||
            typeof f.pretax_income !== "number" ||
            typeof f.depreciation_amortization !== "number" ||
            typeof f.capex !== "number" ||
            typeof f.changes_in_working_capital !== "number"
          ) {
            return null;
          }
          return (
            f.ebit * (1 - f.provision_for_income_taxes / f.pretax_income) +
            f.depreciation_amortization +
            f.capex -
            f.changes_in_working_capital
          ); // ✅ 前端计算
        },
        {
          formatOptions: {
            needFormat: true,
            trimZeros: true,
            roundingMode: bigDecimal.RoundingModes.DOWN,
            precision: 2,
          },
        },
      ),
    },
  ];
  return list;
};
// 测试用
const diffTime = 0;

// 将香港市场Schedule数据转换为 TradingData 数据
const convertHKScheduleDataToTradingData = (
  scheduleData: MarketScheduleResponse["data"],
) => {
  const now = dayjs().valueOf() - diffTime;
  const next_pre_market_open_time =
    scheduleData?.schedule?.next?.phases?.AUCTION?.[1]?.start; // 下一个交易日早盘开市时间 对应香港市场 盘前竞价阶段
  const post_market_close_time =
    scheduleData?.schedule?.current?.phases?.AUCTION?.[1]?.end; // 当前交易日盘后交易结束时间 对应香港市场 盘后竞价阶段
  const pre_market_open_time =
    scheduleData?.schedule?.current?.phases?.AUCTION?.[0]?.start; // 当日早盘开市时间 对应香港市场 盘前竞价阶段
  const previous_post_market_close_time =
    scheduleData?.schedule?.previous?.phases?.AUCTION?.[1]?.end; // 上一个交易日盘后交易结束时间 对应香港市场 盘后竞价阶段
  const previous_regular_market_close_time =
    scheduleData?.schedule?.previous?.phases?.REGULAR_MARKET?.at(-1)?.end; // 上一个交易日盘中交易结束时间 对应香港市场 盘中交易阶段
  const previous_regular_market_open_time =
    scheduleData?.schedule?.previous?.phases?.REGULAR_MARKET?.[0]?.start; // 上一个交易日早盘开市时间 对应香港市场 盘前竞价阶段

  const REGULAR_MARKET =
    scheduleData?.schedule?.current?.phases?.REGULAR_MARKET ?? [];
  const regular_market_open_time = Math.min(
    ...REGULAR_MARKET.map((item) => item.start!),
  ); // 当前交易日盘中开市时间 对应香港市场 盘前竞价阶段
  const regular_market_close_time = Math.max(
    ...REGULAR_MARKET.map((item) => item.end!),
  ); // 当前交易日盘中交易结束时间 对应香港市场 盘中交易阶段
  let current_market_state: StockMarketStateEnum | "LUNCH" | undefined =
    scheduleData?.current_state;
  // if (REGULAR_MARKET?.[1]?.start && now > REGULAR_MARKET?.[1]?.start) {
  // regular_market_open_time = REGULAR_MARKET?.[1]?.start;
  // regular_market_close_time = REGULAR_MARKET?.[1]?.end;
  // }
  // 如果当前时间在上午盘结束下午盘开始这个时间段中 状态改为 lunch
  const lunch_end_time = REGULAR_MARKET?.[1]?.start;
  const lunch_start_time = REGULAR_MARKET?.[0]?.end;
  if (
    !!lunch_start_time &&
    !!lunch_end_time &&
    now > lunch_start_time &&
    now < lunch_end_time
  ) {
    current_market_state = "LUNCH";
  }
  return {
    next_pre_market_open_time,
    post_market_close_time,
    pre_market_open_time,
    previous_post_market_close_time,
    previous_regular_market_close_time,
    previous_regular_market_open_time,
    regular_market_close_time,
    regular_market_open_time,
    current_market_state,
    originSchedule: scheduleData,
    lunch_start_time,
    lunch_end_time,
  };
};

// 将美国市场Schedule数据转换为 TradingData 数据
const convertUSScheduleDataToTradingData = (
  scheduleData: MarketScheduleResponse["data"],
) => {
  const next_pre_market_open_time =
    scheduleData?.schedule?.next?.phases?.PRE_MARKET?.[0]?.start;
  const post_market_close_time =
    scheduleData?.schedule?.current?.phases?.POST_MARKET?.[0]?.end;
  const pre_market_open_time =
    scheduleData?.schedule?.current?.phases?.PRE_MARKET?.[0]?.start;
  const previous_post_market_close_time =
    scheduleData?.schedule?.previous?.phases?.POST_MARKET?.[0]?.end;
  const previous_regular_market_close_time =
    scheduleData?.schedule?.previous?.phases?.REGULAR_MARKET?.[0]?.end;
  const previous_regular_market_open_time =
    scheduleData?.schedule?.previous?.phases?.REGULAR_MARKET?.[0]?.start;
  const regular_market_close_time =
    scheduleData?.schedule?.current?.phases?.REGULAR_MARKET?.[0]?.end;
  const regular_market_open_time =
    scheduleData?.schedule?.current?.phases?.REGULAR_MARKET?.[0]?.start;
  return {
    next_pre_market_open_time,
    post_market_close_time,
    pre_market_open_time,
    previous_post_market_close_time,
    previous_regular_market_close_time,
    previous_regular_market_open_time,
    regular_market_close_time,
    regular_market_open_time,
    current_market_state: scheduleData?.current_state,
    originSchedule: scheduleData,
  };
};

// 将Schedule数据转换为 TradingData 数据
export const convertScheduleDataToTradingData = (
  scheduleData: MarketScheduleResponse["data"],
) => {
  if (scheduleData?.market === StockMarketEnum.HK) {
    return convertHKScheduleDataToTradingData(scheduleData);
  } else if (scheduleData?.market === StockMarketEnum.US) {
    return convertUSScheduleDataToTradingData(scheduleData);
  }
  return null;
};

const FORMAT_TIME = "MMM DD, HH:mm:ss";

// 时间处理 美国市场处理为 GTM-5 香港市场处理为 HKT
export const convertTimeToGTM = (
  time: number | "current",
  market: StockMarketEnum = StockMarketEnum.US,
  format = FORMAT_TIME,
) => {
  if (time === "current") {
    const zone =
      market === StockMarketEnum.US ? "America/New_York" : "Asia/Hong_Kong";
    const nyDate = dayjs().tz(zone);
    const offsetInHours = nyDate.utcOffset() / 60;
    const suffix =
      market === StockMarketEnum.US ? "GMT" + offsetInHours : "HKT";
    return nyDate.format(format) + " " + suffix;
  }
  if (!time) {
    return "";
  }
  if (market === StockMarketEnum.US) {
    const nyDate = dayjs(time).tz("America/New_York");
    const offsetInHours = nyDate.utcOffset() / 60;
    return nyDate.format(format) + " GMT" + offsetInHours;
  } else if (market === StockMarketEnum.HK) {
    return dayjs(time).tz("Asia/Hong_Kong").format(format) + " HKT";
  }
  return "";
};

// 根据currency返回显示不同货币符号
export const getCurrencySymbol = (currency: Stock["currency"]) => {
  if (currency === "CNY") {
    return "¥";
  }
  return "$";
};

// 根据currency返回地区货币符号
export const getRegionCurrencySymbol = (currency: Stock["currency"]) => {
  if (currency === "USD") {
    return "$";
  } else if (currency === "HKD") {
    return "HK$";
  }
  return "CN¥";
};

type IGetStockDisplayNameProps = Pick<
  Stock,
  "market" | "name" | "company_name" | "short_name" | "symbol"
>;
export const getStockDetailTitlePrefix = (
  stockData: IGetStockDisplayNameProps,
) => {
  if (stockData?.market !== StockMarketEnum.HK) {
    return stockData?.symbol;
  }
  if (stockData?.short_name) {
    return `${stockData?.short_name} (${stockData?.symbol})`;
  }
  return stockData?.symbol;
};

export const getStockDetailTitleSuffix = ({
  module,
  market,
}: {
  module: string;
  market?: StockMarketEnum;
}) => {
  if (market === StockMarketEnum.HK) {
    return "";
  }
  if (!module) {
    return I18n.t("Price");
  }
  const moduleMap: Record<string, string> = {
    edgenAI: I18n.t("Edgen AI"),
    forecast: I18n.t("Stock Price Prediction"),
    earning: I18n.t("Earning"),
    dividends: I18n.t("Dividends"),
    financials: I18n.t("Financial"),
  };
  return moduleMap[module] || "";
};
type IHandlePreMarketDataOptions = {
  market?: StockMarketEnum;
  currentPrice: number;
};

// 美股：盘前交易阶段数据展示处理
const handlePreMarketData = (
  marketData: MaybeTradingData = {},
  options: IHandlePreMarketDataOptions,
) => {
  if (options.market === StockMarketEnum.HK) {
    return [];
  }
  // 盘前阶段展示两组价格：上一交易日收盘价和当前价
  const previousChange = marketData!.close! - marketData!.previous_close!;
  const previousChangePercentage =
    (previousChange / (marketData!.previous_close! || 1)) * 100;
  const freshPriceChange = options.currentPrice - marketData!.close!;
  return [
    // 上一交易日收盘价
    {
      current_price: marketData?.close,
      price_change: previousChange ?? 0,
      price_change_percentage: previousChangePercentage,
      marketLabel: I18n.t("At close: %{var0}", {
        var0:
          convertTimeToGTM(
            marketData!.previous_regular_market_close_time!,
            options.market,
          ) || "--",
      }),
    },
    // 当前价
    {
      current_price: options?.currentPrice,
      price_change: freshPriceChange ?? 0,
      price_change_percentage: freshPriceChange
        ? (freshPriceChange / (marketData!.close! || 1)) * 100
        : 0,
      marketLabel: I18n.t("Pre-market: %{var0}", {
        var0: convertTimeToGTM("current", options.market) || "--",
      }), // 时间实时变化
    },
  ];
};

// 港股&美股：盘中阶段数据展示处理
const handleInMarketData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  // 盘中阶段展示一组价格：当前价
  let freshPriceChange = options.currentPrice - marketData!.previous_close!;
  let price_change_percentage = freshPriceChange
    ? (freshPriceChange / (marketData!.previous_close! || 1)) * 100
    : 0;
  if (options.market === StockMarketEnum.HK) {
    const hkBasePrice = marketData!.previous_close!;
    freshPriceChange = options.currentPrice - hkBasePrice;
    price_change_percentage = freshPriceChange
      ? (freshPriceChange / (hkBasePrice || 1)) * 100
      : 0;
  }
  const marketLabel =
    options.market === StockMarketEnum.HK
      ? I18n.t("REGULAR_MARKET:") + " "
      : I18n.t("As of today:");
  return [
    {
      current_price: options?.currentPrice,
      price_change: freshPriceChange ?? 0,
      price_change_percentage: price_change_percentage,
      marketLabel: `${marketLabel}${convertTimeToGTM("current", options.market) || "--"}`,
    },
  ];
};

// 美股：盘后阶段数据展示处理
const handleAfterMarketData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  if (options.market === StockMarketEnum.HK) {
    return [];
  }
  const [closeData, currentData] = handlePreMarketData(marketData, options);
  // 盘后阶段展示的两组价格和盘前阶段逻辑取值相同 label 不同
  return [
    {
      ...closeData,
      marketLabel: I18n.t("At close: %{var0}", {
        var0:
          convertTimeToGTM(
            marketData!.regular_market_close_time!,
            options.market,
          ) || "--",
      }),
    },
    {
      ...currentData,
      marketLabel: I18n.t("Post-market: %{var0}", {
        var0: convertTimeToGTM("current", options.market) || "--",
      }), // 盘后阶段实时变化时间，夜盘状态使用 盘后的收盘时间
    },
  ];
};

// 港股：竞价阶段数据展示处理
const handleAuctionData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  if (options.market !== StockMarketEnum.HK) {
    return [];
  }
  return handleClosedMarketData(marketData, options);
};

// 港股：午餐阶段数据展示处理
const handleLunchData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  if (options.market !== StockMarketEnum.HK) {
    return [];
  }
  const closedMarketData = handleClosedMarketData(marketData, options);
  const firstMarketTime =
    marketData!.regular_market_close_time ||
    marketData!.previous_regular_market_close_time;
  return [
    {
      ...closedMarketData[0],
      marketLabel: I18n.t("LUNCH: %{var0}", {
        var0: convertTimeToGTM(firstMarketTime!, options.market) || "--",
      }),
    },
  ];
};

// 休盘阶段数据展示处理
const handleClosedMarketData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  const now = dayjs().valueOf() - diffTime;
  let firstMarketTime = marketData!.regular_market_close_time!;
  if (now < marketData!.regular_market_open_time!) {
    firstMarketTime = marketData!.previous_regular_market_close_time!;
  }
  if (options.market === StockMarketEnum.HK) {
    const hkBasePrice = marketData!.previous_close!;
    const price_change = marketData!.close! - hkBasePrice;
    const price_change_percentage = price_change
      ? (price_change / (hkBasePrice || 1)) * 100
      : 0;

    return [
      {
        current_price: marketData?.close,
        price_change: price_change ?? 0,
        price_change_percentage: price_change_percentage,
        marketLabel: I18n.t("CLOSED_MARKET: %{var0}", {
          var0: convertTimeToGTM(firstMarketTime!, options.market) || "--",
        }),
      },
    ];
  }
  const [closeData, currentData] = handlePreMarketData(marketData, options);
  // 美股：休盘阶段展示的两组价格和盘前阶段逻辑取值相同 label 不同
  const lastMarketTime =
    marketData!.post_market_close_time ||
    marketData!.previous_post_market_close_time;
  return [
    {
      ...closeData,
      marketLabel: I18n.t("At close: %{var0}", {
        var0: convertTimeToGTM(firstMarketTime!, options.market) || "--",
      }),
    },
    {
      ...currentData,
      marketLabel: I18n.t("Post-market: %{var0}", {
        var0: convertTimeToGTM(lastMarketTime!, options.market) || "--",
      }), // 盘后阶段实时变化时间，夜盘状态使用 盘后的收盘时间
    },
  ];
};

// 股票不同交易阶段数据展示处理
export const handleStockMarketData = (
  marketData: MaybeTradingData,
  options: IHandlePreMarketDataOptions,
) => {
  if (marketData?.current_market_state === StockMarketStateEnum.CLOSED_MARKET) {
    return handleClosedMarketData(marketData, options);
  }
  if (marketData?.current_market_state === StockMarketStateEnum.PRE_MARKET) {
    return handlePreMarketData(marketData, options);
  }
  if (
    marketData?.current_market_state === StockMarketStateEnum.REGULAR_MARKET
  ) {
    return handleInMarketData(marketData, options);
  }
  if (marketData?.current_market_state === StockMarketStateEnum.POST_MARKET) {
    return handleAfterMarketData(marketData, options);
  }
  if (marketData?.current_market_state === StockMarketStateEnum.AUCTION) {
    return handleAuctionData(marketData, options);
  }
  if (marketData?.current_market_state === "LUNCH") {
    return handleLunchData(marketData, options);
  }
  return [];
};
